import { useCallback, useEffect, useState } from "react";
import { driver } from "driver.js";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import "driver.js/dist/driver.css";

export function useOnboardingTour() {
  const { user } = useAuth();
  const [hasCompletedTour, setHasCompletedTour] = useState(true);

  useEffect(() => {
    const checkTourStatus = async () => {
      if (!user) return;

      const { data } = await supabase
        .from("user_preferences")
        .select("setup_completed")
        .eq("user_id", user.id)
        .single();

      setHasCompletedTour(data?.setup_completed ?? false);
    };

    checkTourStatus();
  }, [user]);

  const markTourAsCompleted = useCallback(async () => {
    if (!user) return;

    await supabase
      .from("user_preferences")
      .update({ setup_completed: true })
      .eq("user_id", user.id);

    setHasCompletedTour(true);
  }, [user]);

  const startMainTour = useCallback(() => {
    const driverObj = driver({
      showProgress: true,
      steps: [
        {
          element: "#dashboard-header",
          popover: {
            title: "Welcome to Subtrackr! 👋",
            description:
              "Let's take a quick tour to help you get started with managing your subscriptions and contracts. <a href='/documentation' target='_blank' style='color: hsl(var(--primary)); text-decoration: underline;'>View full documentation</a>",
            side: "bottom",
            align: "start",
          },
        },
        {
          element: "[data-nav-item='contracts']",
          popover: {
            title: "Navigation Menu",
            description:
              "Access all main features from here - Contracts, Dashboard, and Setup & Manage sections.",
            side: "right",
            align: "start",
          },
        },
        {
          element: "#dashboard-widgets",
          popover: {
            title: "Your Workspace",
            description:
              "This is your dashboard where you can see all your contract statistics and upcoming renewals at a glance.",
            side: "top",
            align: "start",
          },
        },
        {
          popover: {
            title: "Ready to Create Your First Contract?",
            description:
              "Great! Let's explore the different ways you can add contracts to the system. <a href='/documentation#creating-contracts' target='_blank' style='color: hsl(var(--primary)); text-decoration: underline;'>Learn more</a>",
            side: "top",
            align: "start",
          },
        },
      ],
      onDestroyed: () => {
        markTourAsCompleted();
      },
    });

    driverObj.drive();
  }, [markTourAsCompleted]);

  const startContractsTour = useCallback(() => {
    const driverObj = driver({
      showProgress: true,
      steps: [
        {
          element: "[data-nav-item='contracts']",
          popover: {
            title: "Contracts Menu",
            description:
              "Click here to access your contracts. You'll see all your active subscriptions and agreements.",
            side: "right",
            align: "start",
          },
        },
        {
          popover: {
            title: "Three Ways to Create Contracts",
            description:
              "You can add contracts using:<br/>• <strong>Quick Add</strong> - Fast entry for essential details<br/>• <strong>Full Form</strong> - Complete contract information<br/>• <strong>AI Extraction</strong> - Upload documents and let AI extract the details<br/><br/><a href='/documentation#quick-add' target='_blank' style='color: hsl(var(--primary)); text-decoration: underline;'>Learn more</a>",
            side: "top",
            align: "start",
          },
        },
        {
          popover: {
            title: "Let's Start with Quick Add",
            description:
              "We'll guide you through creating your first contract using the Quick Add feature. <a href='/documentation#quick-add' target='_blank' style='color: hsl(var(--primary)); text-decoration: underline;'>View guide</a>",
            side: "top",
            align: "start",
          },
        },
      ],
    });

    driverObj.drive();
  }, []);

  const startFirstContractTour = useCallback(() => {
    const driverObj = driver({
      showProgress: true,
      steps: [
        {
          element: "#supplier_name",
          popover: {
            title: "Vendor Name",
            description:
              "Enter the name of the company or service you're paying for (e.g., Netflix, Microsoft 365).",
            side: "bottom",
            align: "start",
          },
        },
        {
          element: "#expected_cost",
          popover: {
            title: "Cost",
            description:
              "Enter how much you pay per billing cycle. You can change the currency in settings.",
            side: "bottom",
            align: "start",
          },
        },
        {
          element: "[name='payment_frequency']",
          popover: {
            title: "Payment Frequency",
            description:
              "How often do you pay? Monthly, yearly, or custom intervals - it's all supported.",
            side: "bottom",
            align: "start",
          },
        },
        {
          element: "#expiry_date",
          popover: {
            title: "Expiry Date",
            description:
              "When does this contract end or renew? We'll send you reminders before it expires.",
            side: "bottom",
            align: "start",
          },
        },
        {
          element: "[type='submit']",
          popover: {
            title: "Save Your Contract",
            description:
              "Once you're done, click Save! You can always edit or add more details later. <a href='/documentation#managing-contracts' target='_blank' style='color: hsl(var(--primary)); text-decoration: underline;'>Learn more</a>",
            side: "top",
            align: "start",
          },
        },
      ],
    });

    driverObj.drive();
  }, []);

  return {
    hasCompletedTour,
    startMainTour,
    startContractsTour,
    startFirstContractTour,
  };
}
